/**
 * LeGeekShop — traducteur auto gratuit + détection géo (pays → langue).
 */
(function () {
  const CFG = window.LGK_TRANSLATE || {};
  const SOURCE = CFG.source || 'fr';
  const STORAGE_LANG = 'lgk_translate_lang';
  const STORAGE_MANUAL = 'lgk_translate_manual';
  const STORAGE_CACHE = 'lgk_translate_cache_v3';
  const STORAGE_GEO_COUNTRY = 'lgk_geo_country';
  const STORAGE_GEO_TRIED = 'lgk_geo_country_tried';
  const STORAGE_GEO_REDIRECT = 'lgk_geo_redirect_done';
  const STORAGE_QUOTA_DISMISSED = 'lgk_quota_alert_dismissed';
  const QUOTA_ALERT_THRESHOLD = CFG.quotaAlertThreshold ?? 0.1;
  const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE', 'SVG', 'TEMPLATE']);
  const ATTRS = ['placeholder', 'aria-label', 'title', 'alt'];
  const BATCH = CFG.batchSize || 50;
  const API_CONCURRENCY = CFG.apiConcurrency || 5;
  const MAX_TEXTS = CFG.maxTexts || 320;
  const FETCH_TIMEOUT_MS = CFG.fetchTimeoutMs || 20000;
  const SITE_ROOTS = CFG.siteRoots || ['#shopify-section-group-header-group', '#main', '#shopify-section-group-footer-group'];

  /** Aligné sur Worker country-lang.js (source de vérité serveur). */
  const COUNTRY_LANG = {
    FR: 'fr', BE: 'fr', LU: 'fr', MC: 'fr',
    GF: 'fr', GP: 'fr', MQ: 'fr', RE: 'fr', YT: 'fr', NC: 'fr', PF: 'fr', PM: 'fr', WF: 'fr', BL: 'fr', MF: 'fr',
    US: 'en', GB: 'en', IE: 'en', AU: 'en', NZ: 'en',
    ZA: 'en', IN: 'en', SG: 'en', PH: 'en', NG: 'en', KE: 'en', GH: 'en', JM: 'en', TT: 'en', MT: 'en', CY: 'en',
    ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es', VE: 'es', EC: 'es', GT: 'es', CU: 'es',
    BO: 'es', DO: 'es', HN: 'es', PY: 'es', SV: 'es', NI: 'es', CR: 'es', PA: 'es', UY: 'es', PR: 'es',
    DE: 'de', AT: 'de', LI: 'de',
    IT: 'it', SM: 'it', VA: 'it',
    PT: 'pt', BR: 'pt', AO: 'pt', MZ: 'pt',
    NL: 'nl', SR: 'nl',
    PL: 'pl',
    RU: 'ru', BY: 'ru', KZ: 'ru', KG: 'ru',
    SA: 'ar', AE: 'ar', EG: 'ar', MA: 'ar', DZ: 'ar', TN: 'ar', LB: 'ar', JO: 'ar',
    KW: 'ar', QA: 'ar', BH: 'ar', OM: 'ar', IQ: 'ar', SY: 'ar', YE: 'ar', LY: 'ar', SD: 'ar',
    CN: 'zh', TW: 'zh', HK: 'zh', MO: 'zh',
    JP: 'ja',
    KR: 'ko',
    TR: 'tr',
  };

  /** Pays où l'anglais est la langue boutique par défaut (redir /en autorisée). */
  const ENGLISH_MARKET_COUNTRIES = new Set(['US', 'GB', 'AU', 'IE', 'NZ']);
  /** Pays francophones : jamais de redirection auto vers /en. */
  const FRENCH_MARKET_COUNTRIES = new Set([
    'FR', 'BE', 'LU', 'MC', 'GF', 'GP', 'MQ', 'RE', 'YT', 'NC', 'PF', 'PM', 'WF', 'BL', 'MF',
  ]);

  let activeLang = SOURCE;
  /** Langue réelle du DOM affiché (fr natif, puis en/es/… après traduction). */
  let contentBaseLang = SOURCE;
  let cache = loadCache();
  let busy = false;
  let translating = false;
  let observer = null;

  function loadCache() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_CACHE) || '{}');
    } catch {
      return {};
    }
  }

  function saveCache() {
    try {
      const keys = Object.keys(cache);
      if (keys.length > 3000) keys.slice(0, 800).forEach((k) => delete cache[k]);
      localStorage.setItem(STORAGE_CACHE, JSON.stringify(cache));
    } catch {
      /* quota */
    }
  }

  function cacheId(source, lang, text) {
    return `${source}>${lang}::${text}`;
  }

  function isOnEnPath() {
    return /^\/en(\/|$)/.test(window.location.pathname);
  }

  function getDomSourceLang() {
    return contentBaseLang;
  }

  const availableCountries = new Set((CFG.countries || []).map((c) => c.iso));

  function countryMatchesLang(iso, lang) {
    const code = String(lang || '').toLowerCase().split('-')[0];
    if (!iso || !code) return false;
    if (resolveCountryLang(iso, '') === code) return true;
    const prefs = CFG.langCountries?.[code] || [];
    return prefs.includes(iso);
  }

  function resolveCountryForLang(lang, hintCountry) {
    if (CFG.syncCurrency === false) return null;
    const code = String(lang || '').toLowerCase().split('-')[0];
    const hint = hintCountry && availableCountries.has(hintCountry) ? hintCountry : null;
    if (hint && countryMatchesLang(hint, code)) return hint;
    const prefs = CFG.langCountries?.[code];
    if (!prefs?.length) return null;
    for (const iso of prefs) {
      if (availableCountries.has(iso)) return iso;
    }
    return null;
  }

  function needsCountryChange(lang, hintCountry) {
    const target = resolveCountryForLang(lang, hintCountry);
    if (!target || target === CFG.currentCountry) return null;
    return target;
  }

  function geoAlreadyTried(country) {
    try {
      return sessionStorage.getItem(STORAGE_GEO_TRIED) === country;
    } catch {
      return false;
    }
  }

  function markGeoTried(country) {
    try {
      sessionStorage.setItem(STORAGE_GEO_TRIED, country);
    } catch {
      /* ignore */
    }
  }

  function urlCountryParam() {
    return new URLSearchParams(window.location.search).get('country')?.toUpperCase() || null;
  }

  function submitLocalizationForm(countryCode, localeCode) {
    const form = document.querySelector('[data-lgk-localization-fallback] form')
      || document.querySelector('[data-lgk-currency] form')
      || document.querySelector('form[action*="localization"]');
    if (!form) return false;

    const setHidden = (name, value) => {
      let el = form.querySelector(`input[name="${name}"][data-lgk-auto]`);
      if (!el) {
        el = document.createElement('input');
        el.type = 'hidden';
        el.name = name;
        el.dataset.lgkAuto = '1';
        form.appendChild(el);
      }
      el.value = value;
    };

    if (countryCode) setHidden('country_code', countryCode);
    if (localeCode) setHidden('locale_code', localeCode);

    // Sortir de /en quand on force la locale FR
    if (localeCode === 'fr' && isOnEnPath()) {
      setHidden('return_to', frPathFromCurrent());
    }

    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
    return true;
  }

  function submitCountryChangeViaForm(countryCode) {
    const forms = document.querySelectorAll(
      '[data-lgk-currency] form, [data-lgk-localization-fallback] form, form[action*="localization"]',
    );
    for (const form of forms) {
      const btn = form.querySelector(`button[name="country_code"][value="${countryCode}"]`);
      if (btn) {
        btn.click();
        return true;
      }
    }
    const form = forms[0];
    if (!form) return false;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'country_code';
    input.value = countryCode;
    form.appendChild(input);
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
    return true;
  }

  async function submitCountryChangeFetch(countryCode) {
    const root = window.Shopify?.routes?.root || '/';
    const params = new URLSearchParams();
    params.set('form_type', 'localization');
    params.set('utf8', '✓');
    params.set('country_code', countryCode);
    params.set('return_to', window.location.pathname + window.location.search + window.location.hash);
    const token = document.querySelector('input[name="authenticity_token"]')?.value;
    if (token) params.set('authenticity_token', token);
    try {
      const res = await fetch(`${root}localization`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
        credentials: 'same-origin',
        redirect: 'manual',
      });
      return res.type === 'opaqueredirect' || res.status === 302 || res.status === 303;
    } catch {
      return false;
    }
  }

  async function submitCountryChange(countryCode, opts = {}) {
    if (!countryCode || countryCode === CFG.currentCountry) return false;

    if (!opts.force && geoAlreadyTried(countryCode) && CFG.currentCountry !== countryCode) {
      return false;
    }

    markGeoTried(countryCode);

    const locale = opts.locale || null;
    if (submitLocalizationForm(countryCode, locale)) return true;
    if (submitCountryChangeViaForm(countryCode)) return true;

    const ok = await submitCountryChangeFetch(countryCode);
    if (ok) window.location.reload();
    return ok;
  }

  function mergeGeoSignals(geo) {
    let { language, country } = geo;
    const urlC = urlCountryParam();

    if (urlC) {
      country = urlC;
      language = resolveCountryLang(urlC, navigator.language) || language;
      if (urlC === 'US') language = 'en';
    }

    if (country && !language) {
      language = resolveCountryLang(country, navigator.language);
    }

    // Pays francophone → forcer FR (ignore Accept-Language en du navigateur)
    if (country && FRENCH_MARKET_COUNTRIES.has(country)) {
      language = 'fr';
    }

    return { language: language || SOURCE, country: country || null };
  }

  /**
   * Redir /en uniquement pour les marchés EN (US/GB/AU…).
   * Ne pas se baser sur Accept-Language seul (Chrome en + IP FR = faux positif).
   */
  function geoWantsEnglish(country, language) {
    if (country && FRENCH_MARKET_COUNTRIES.has(country)) return false;
    if (country && ENGLISH_MARKET_COUNTRIES.has(country)) return true;
    // Sans pays fiable : ne jamais forcer l'anglais auto
    if (!country) return false;
    return language === 'en';
  }

  async function runGeoAuto(manual) {
    if (CFG.geoAuto === false) return false;

    const merged = mergeGeoSignals(await detectGeoLanguage());
    const { language, country: geoCountry } = merged;

    if (geoCountry) {
      try {
        sessionStorage.setItem(STORAGE_GEO_COUNTRY, geoCountry);
      } catch {
        /* ignore */
      }
    }

    const geoLang = language !== SOURCE ? language : null;
    const wantsEn = geoWantsEnglish(geoCountry, geoLang);
    const targetLang = wantsEn ? 'en' : geoLang;
    const countryMismatch = geoCountry && geoCountry !== CFG.currentCountry;
    const urlStuck = urlCountryParam() === geoCountry && countryMismatch;

    // Choix manuel (FR) toujours respecté — plus de forçage geoOverride
    if (manual) {
      // Sync devise/pays éventuel sans changer la langue
      if (countryMismatch && !geoAlreadyTried(geoCountry) && !urlStuck && !wantsEn) {
        markGeoTried(geoCountry);
        if (submitLocalizationForm(geoCountry, null)) return true;
      }
      return false;
    }

    if (targetLang) localStorage.setItem(STORAGE_LANG, targetLang);
    // Visiteur FR détecté : effacer un vieux « en » en localStorage
    if (geoCountry && FRENCH_MARKET_COUNTRIES.has(geoCountry) && !wantsEn) {
      localStorage.setItem(STORAGE_LANG, SOURCE);
      try {
        sessionStorage.removeItem(STORAGE_GEO_REDIRECT);
      } catch {
        /* ignore */
      }
    }

    // Toujours sortir de /en (anglais = DOM, jamais d'URL Shopify locale)
    if (isOnEnPath()) {
      let bounced = false;
      try {
        bounced = sessionStorage.getItem('lgk_fr_bounce_done') === '1';
      } catch {
        /* ignore */
      }
      if (!bounced) {
        try {
          sessionStorage.setItem('lgk_fr_bounce_done', '1');
        } catch {
          /* ignore */
        }
        localStorage.setItem(STORAGE_LANG, SOURCE);
        setWantEnCookie(false);
        const frCountry =
          geoCountry && FRENCH_MARKET_COUNTRIES.has(geoCountry) ? geoCountry : 'FR';
        if (submitLocalizationForm(frCountry, 'fr')) return true;
        window.location.replace(frPathFromCurrent());
        return true;
      }
    }

    // Pays via formulaire Shopify — JAMAIS locale_code=en (provoque /en + boucle Worker)
    if (countryMismatch && !geoAlreadyTried(geoCountry) && !urlStuck) {
      markGeoTried(geoCountry);
      if (submitLocalizationForm(geoCountry, null)) return true;
    }

    // Marché EN : traduction DOM uniquement — JAMAIS de redirect auto vers /en.
    // (Worker CF strip /en sans cookie lgk_want_en → boucle reload sinon.)
    if (wantsEn && !isOnEnPath()) {
      localStorage.setItem(STORAGE_LANG, 'en');
      activeLang = 'en';
      contentBaseLang = SOURCE;
      setHtmlLang('en');
      updateSwitcherUI('en');
      setBusy(true);
      try {
        await runDomTranslation('en', SOURCE);
      } catch (err) {
        console.warn('[LGK Translate] geo en', err);
      } finally {
        setBusy(false);
      }
      return true;
    }

    if (targetLang && targetLang !== SOURCE && !isOnEnPath()) {
      activeLang = targetLang;
      contentBaseLang = SOURCE;
      setHtmlLang(targetLang);
      updateSwitcherUI(targetLang);
      setBusy(true);
      try {
        await runDomTranslation(targetLang, SOURCE);
      } catch (err) {
        console.warn('[LGK Translate] geo lang', err);
      } finally {
        setBusy(false);
      }
      return true;
    }

    return false;
  }

  function siteRoots() {
    const roots = [];
    for (const sel of SITE_ROOTS) {
      const el = document.querySelector(sel);
      if (el && !roots.includes(el)) roots.push(el);
    }
    return roots.length ? roots : [document.body];
  }

  function shouldSkipNode(node) {
    if (!node?.parentElement) return true;
    if (node.data && !String(node.data).trim()) return true;
    let p = node.parentElement;
    while (p) {
      if (p.closest('[data-lgk-no-translate]')) return true;
      if (SKIP_TAGS.has(p.tagName)) return true;
      p = p.parentElement;
    }
    return false;
  }

  function isNodeVisible(node) {
    const el = node.parentElement;
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight + 200;
  }

  function collectTextNodes(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const visible = [];
    const hidden = [];
    let n;
    while ((n = walker.nextNode())) {
      if (shouldSkipNode(n)) continue;
      const t = n.data.trim();
      if (t.length < 2) continue;
      if (/^[\d\s.,€$£%+/-]+$/.test(t)) continue;
      if (/^https?:\/\//i.test(t)) continue;
      (isNodeVisible(n) ? visible : hidden).push(n);
    }
    return visible.concat(hidden);
  }

  function collectAttrItems(root) {
    const items = [];
    root.querySelectorAll(ATTRS.map((a) => `[${a}]`).join(',')).forEach((el) => {
      if (el.closest('[data-lgk-no-translate]')) return;
      for (const attr of ATTRS) {
        const val = el.getAttribute(attr);
        if (!val || val.trim().length < 2) continue;
        if (/^https?:\/\//i.test(val.trim())) continue;
        items.push({ el, attr, text: val });
      }
    });
    return items;
  }

  function uniqueTexts(nodes) {
    const map = new Map();
    for (const node of nodes) {
      const trimmed = node.data.trim();
      if (!map.has(trimmed)) map.set(trimmed, []);
      map.get(trimmed).push(node);
    }
    return limitMap(map, MAX_TEXTS);
  }

  function uniqueAttrTexts(items) {
    const map = new Map();
    for (const item of items) {
      const trimmed = item.text.trim();
      if (!map.has(trimmed)) map.set(trimmed, []);
      map.get(trimmed).push(item);
    }
    return limitMap(map, Math.max(40, MAX_TEXTS - map.size));
  }

  function limitMap(map, max) {
    if (map.size <= max) return map;
    const out = new Map();
    let i = 0;
    for (const [k, v] of map) {
      if (i++ >= max) break;
      out.set(k, v);
    }
    return out;
  }

  function applyTextNodes(textMap, text, translated) {
    const nodes = textMap.get(text);
    if (!nodes) return;
    for (const node of nodes) {
      if (!node.__lgkOriginal) node.__lgkOriginal = node.data;
      const lead = node.data.match(/^\s*/)[0];
      const trail = node.data.match(/\s*$/)[0];
      node.data = lead + translated + trail;
    }
  }

  function applyAttrItems(attrMap, text, translated) {
    const items = attrMap.get(text);
    if (!items) return;
    for (const { el, attr } of items) {
      const key = `__lgkAttr_${attr}`;
      if (!el[key]) el[key] = el.getAttribute(attr);
      el.setAttribute(attr, translated);
    }
  }

  async function translateChunksParallel(missing, source, target, onChunk) {
    if (!missing.length) return;
    const chunks = [];
    for (let i = 0; i < missing.length; i += BATCH) {
      chunks.push(missing.slice(i, i + BATCH));
    }
    let cursor = 0;
    async function worker() {
      while (cursor < chunks.length) {
        const i = cursor++;
        await onChunk(chunks[i]);
      }
    }
    await Promise.all(Array.from({ length: Math.min(API_CONCURRENCY, chunks.length) }, worker));
  }

  async function fetchJson(url, options = {}) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
    try {
      const res = await fetch(url, { ...options, signal: ctrl.signal });
      return res;
    } finally {
      clearTimeout(timer);
    }
  }

  async function myMemoryOne(text, source, target) {
    const pair = `${source}|${target}`;
    const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text.slice(0, 450))}&langpair=${pair}&de=contact@legeekshop.com&mt=1`;
    const res = await fetchJson(url);
    const data = await res.json();
    const translated = data?.responseData?.translatedText;
    if (Number(data?.responseStatus ?? 0) === 200 && translated && translated.trim() !== text.trim()) {
      return translated;
    }
    return text;
  }

  async function myMemoryBatch(texts, source, target) {
    const out = new Array(texts.length);
    let idx = 0;
    const workers = 3;
    async function run() {
      while (idx < texts.length) {
        const i = idx++;
        const text = texts[i];
        const id = cacheId(source, target, text);
        if (cache[id]) {
          out[i] = cache[id];
          continue;
        }
        try {
          const translated = await myMemoryOne(text, source, target);
          if (translated.trim() !== text.trim()) cache[id] = translated;
          out[i] = translated;
        } catch {
          out[i] = text;
        }
        await new Promise((r) => setTimeout(r, 40));
      }
    }
    await Promise.all(Array.from({ length: workers }, run));
    saveCache();
    return out;
  }

  async function apiTranslate(texts, source, target) {
    if (source === target) return texts;
    const api = CFG.apiBase?.replace(/\/$/, '');
    if (api) {
      try {
        const res = await fetchJson(`${api}/translate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ source, target, texts }),
        });
        if (res.ok) {
          const data = await res.json();
          const translations = data.translations;
          if (Array.isArray(translations) && translations.length === texts.length) {
            return translations;
          }
        }
      } catch {
        /* fallback */
      }
    }
    if (source === SOURCE) {
      return myMemoryBatch(texts, source, target);
    }
    return texts;
  }

  async function translateMap(textMap, source, target) {
    if (!textMap.size) return;
    const missing = [];

    for (const text of textMap.keys()) {
      const id = cacheId(source, target, text);
      const hit = cache[id];
      if (hit && hit.trim() !== text.trim()) {
        applyTextNodes(textMap, text, hit);
      } else {
        missing.push(text);
      }
    }

    await translateChunksParallel(missing, source, target, async (chunk) => {
      const translated = await apiTranslate(chunk, source, target);
      chunk.forEach((text, idx) => {
        const val = translated[idx] || text;
        if (val.trim() !== text.trim()) cache[cacheId(source, target, text)] = val;
        applyTextNodes(textMap, text, val);
      });
    });
    saveCache();
  }

  async function translateAttrMap(attrMap, source, target) {
    if (!attrMap.size) return;
    const missing = [];

    for (const text of attrMap.keys()) {
      const id = cacheId(source, target, text);
      const hit = cache[id];
      if (hit && hit.trim() !== text.trim()) {
        applyAttrItems(attrMap, text, hit);
      } else {
        missing.push(text);
      }
    }

    await translateChunksParallel(missing, source, target, async (chunk) => {
      const translated = await apiTranslate(chunk, source, target);
      chunk.forEach((text, idx) => {
        const val = translated[idx] || text;
        if (val.trim() !== text.trim()) cache[cacheId(source, target, text)] = val;
        applyAttrItems(attrMap, text, val);
      });
    });
    saveCache();
  }

  async function translateFullSite(target, source) {
    if (translating) return;
    translating = true;
    try {
      const allNodes = [];
      const allAttrs = [];
      for (const root of siteRoots()) {
        allNodes.push(...collectTextNodes(root));
        allAttrs.push(...collectAttrItems(root));
      }
      await Promise.all([
        translateMap(uniqueTexts(allNodes), source, target),
        translateAttrMap(uniqueAttrTexts(allAttrs), source, target),
      ]);
    } finally {
      translating = false;
    }
  }

  function restoreAll() {
    for (const root of siteRoots()) {
      for (const node of collectTextNodes(root)) {
        if (node.__lgkOriginal) {
          node.data = node.__lgkOriginal;
          delete node.__lgkOriginal;
        }
      }
      for (const { el, attr } of collectAttrItems(root)) {
        const key = `__lgkAttr_${attr}`;
        if (el[key]) {
          el.setAttribute(attr, el[key]);
          delete el[key];
        }
      }
    }
    contentBaseLang = SOURCE;
  }

  function setHtmlLang(lang) {
    document.documentElement.lang = lang;
    document.documentElement.dir = ['ar', 'he', 'fa', 'ur'].includes(lang) ? 'rtl' : 'ltr';
  }

  function shopifyLocaleUrl(lang) {
    const locales = CFG.shopifyLocales || {};
    if (!locales[lang]) return null;
    const root = locales[lang].root || `/${lang}`;
    const path = window.location.pathname.replace(/^\/(en|fr)(\/|$)/, '/');
    return `${root}${path === '/' ? '' : path}`;
  }

  function isAlreadyOnShopifyLocale(lang) {
    if (lang !== 'en') return false;
    return isOnEnPath();
  }

  function frPathFromCurrent() {
    const path = window.location.pathname.replace(/^\/en(\/|$)/, '/');
    return (path || '/') + window.location.search + window.location.hash;
  }

  function resolveCountryLang(country, acceptLang) {
    const cc = String(country || '').toUpperCase();
    if (!cc) return null;
    if (cc === 'CA' || cc === 'CH') {
      const nav = (acceptLang || navigator.language || '').split('-')[0].toLowerCase();
      const opts = cc === 'CA' ? ['fr', 'en'] : ['fr', 'de', 'it'];
      if (opts.includes(nav)) return nav;
      return opts[0];
    }
    return COUNTRY_LANG[cc] || null;
  }

  async function detectGeoLanguage() {
    const api = CFG.apiBase?.replace(/\/$/, '');
    if (api) {
      try {
        const res = await fetchJson(`${api}/geo`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (res.ok) {
          const data = await res.json();
          return { language: data.language || SOURCE, country: data.country || null };
        }
      } catch {
        /* ignore */
      }
    }
    const nav = (navigator.language || '').split('-');
    const navCountry = nav[1]?.toUpperCase();
    const navLang = nav[0]?.toLowerCase();
    if (navCountry && availableCountries.has(navCountry)) {
      return {
        language: resolveCountryLang(navCountry, navigator.language) || navLang || SOURCE,
        country: navCountry,
      };
    }
    return { language: SOURCE, country: null };
  }

  function updateSwitcherUI(lang) {
    document.querySelectorAll('[data-lgk-translate-option]').forEach((btn) => {
      const on = btn.getAttribute('data-lang') === lang;
      btn.setAttribute('aria-selected', on ? 'true' : 'false');
      btn.classList.toggle('is-active', on);
    });
    document.querySelectorAll('[data-lgk-translate-label]').forEach((label) => {
      const def = CFG.languages?.find((l) => l.code === lang);
      if (def) label.textContent = def.label;
    });
  }

  function setBusy(on) {
    busy = on;
    document.documentElement.classList.toggle('lgk-translate-busy', on);
  }

  async function runDomTranslation(targetLang, sourceLang) {
    if (targetLang === sourceLang) return;
    await Promise.race([
      translateFullSite(targetLang, sourceLang),
      new Promise((resolve) => setTimeout(resolve, 90000)),
    ]);
    contentBaseLang = targetLang;
  }

  function setWantEnCookie(on) {
    try {
      if (on) {
        document.cookie = 'lgk_want_en=1; Path=/; Max-Age=31536000; SameSite=Lax';
      } else {
        document.cookie = 'lgk_want_en=; Path=/; Max-Age=0; SameSite=Lax';
      }
    } catch {
      /* ignore */
    }
  }

  async function applyLanguage(lang, opts = {}) {
    if (busy) return;

    if (opts.manual) localStorage.setItem(STORAGE_MANUAL, '1');
    localStorage.setItem(STORAGE_LANG, lang);

    // Cookie lu par le Worker CF : seul moyen d'autoriser /en face au strip Markets
    if (opts.manual) {
      setWantEnCookie(lang === 'en');
    } else if (lang === SOURCE) {
      setWantEnCookie(false);
    }

    if (!opts.skipCountry && opts.manual) {
      const hint = sessionStorage.getItem(STORAGE_GEO_COUNTRY);
      const target = resolveCountryForLang(lang, hint);
      // Jamais locale_code=en ici : on reste sur l'URL FR + traduction DOM
      if (target && target !== CFG.currentCountry) {
        if (await submitCountryChange(target, { force: true, locale: null })) return;
      }
    }

    setBusy(true);

    try {
      activeLang = lang;
      setHtmlLang(lang);
      updateSwitcherUI(lang);

      // Retour français natif
      if (lang === SOURCE) {
        try {
          sessionStorage.removeItem(STORAGE_GEO_REDIRECT);
          sessionStorage.removeItem(STORAGE_GEO_TRIED);
          sessionStorage.removeItem('lgk_fr_bounce_done');
        } catch {
          /* ignore */
        }
        setWantEnCookie(false);
        if (opts.manual && isOnEnPath()) {
          window.location.replace(frPathFromCurrent());
          return;
        }
        restoreAll();
        return;
      }

      // Anglais / autres : traduction DOM uniquement — PAS de préfixe /en (anti-boucle Markets/Worker)
      if (lang === 'en') {
        setWantEnCookie(false);
        if (isOnEnPath()) {
          window.location.replace(frPathFromCurrent());
          return;
        }
        if (lang === contentBaseLang) return;
        await runDomTranslation('en', contentBaseLang);
        return;
      }

      if (lang === contentBaseLang) return;

      await runDomTranslation(lang, contentBaseLang);
    } catch (err) {
      console.warn('[LGK Translate]', err);
    } finally {
      setBusy(false);
    }
  }

  const panelHomes = new WeakMap();
  let openPanelEl = null;
  let openToggleEl = null;
  let openWrapEl = null;

  function rememberPanelHome(panel) {
    if (!panelHomes.has(panel)) {
      panelHomes.set(panel, { parent: panel.parentElement, next: panel.nextSibling });
    }
  }

  function restorePanelHome(panel) {
    const home = panelHomes.get(panel);
    if (home?.parent) home.parent.insertBefore(panel, home.next || null);
  }

  function positionFloatingPanel(toggle, panel, wrap) {
    const rect = toggle.getBoundingClientRect();
    const openUp = Boolean(wrap?.closest('.header-sidebar__footer'));
    const gap = 6;
    panel.classList.add('lgk-translate__panel--floating');
    panel.style.cssText = [
      'position:fixed',
      'z-index:100000',
      `right:${Math.max(8, window.innerWidth - rect.right)}px`,
      'left:auto',
      `min-width:${Math.max(rect.width, 176)}px`,
      openUp
        ? `bottom:${Math.max(8, window.innerHeight - rect.top + gap)}px;top:auto`
        : `top:${rect.bottom + gap}px;bottom:auto`,
    ].join(';');
  }

  function clearFloatingPanel(panel) {
    panel.classList.remove('lgk-translate__panel--floating');
    panel.style.cssText = '';
  }

  function closeAllPanels() {
    document.querySelectorAll('[data-lgk-translate-panel]').forEach((panel) => {
      panel.hidden = true;
      clearFloatingPanel(panel);
      restorePanelHome(panel);
    });
    document.querySelectorAll('[data-lgk-translate-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    openPanelEl = null;
    openToggleEl = null;
    openWrapEl = null;
  }

  function bindSwitcher() {
    document.querySelectorAll('[data-lgk-translate-option]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const lang = btn.getAttribute('data-lang');
        closeAllPanels();
        if (lang) applyLanguage(lang, { manual: true });
      });
    });

    document.querySelectorAll('[data-lgk-translate]').forEach((wrap) => {
      const toggle = wrap.querySelector('[data-lgk-translate-toggle]');
      const panel = wrap.querySelector('[data-lgk-translate-panel]');
      if (!toggle || !panel) return;
      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const willOpen = panel.hidden;
        if (window.LGKCurrency?.closeAll) window.LGKCurrency.closeAll();
        closeAllPanels();
        if (!willOpen) return;
        rememberPanelHome(panel);
        document.body.appendChild(panel);
        positionFloatingPanel(toggle, panel, wrap);
        panel.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
        openPanelEl = panel;
        openToggleEl = toggle;
        openWrapEl = wrap;
      });
    });

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-lgk-translate]') || e.target.closest('[data-lgk-translate-panel]')) return;
      closeAllPanels();
    });

    const reposition = () => {
      if (openPanelEl && !openPanelEl.hidden && openToggleEl && openWrapEl) {
        positionFloatingPanel(openToggleEl, openPanelEl, openWrapEl);
      }
    };
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
  }

  function formatChars(n) {
    return Number(n || 0).toLocaleString('fr-FR');
  }

  function isMerchantView() {
    if (CFG.quotaAlertMerchantOnly === false) return true;
    if (window.Shopify?.designMode) return true;
    if (window.Shopify?.previewTheme != null) return true;
    if (new URLSearchParams(window.location.search).get('lgk_quota') === '1') return true;
    if (document.querySelector('#PBarNextFrameWrapper, #admin-bar-iframe')) return true;
    return false;
  }

  function dismissQuotaAlert() {
    try {
      sessionStorage.setItem(STORAGE_QUOTA_DISMISSED, '1');
    } catch {
      /* ignore */
    }
    document.querySelector('.lgk-translate-quota-bar')?.remove();
  }

  function showQuotaAlert(message, level) {
    if (document.querySelector('.lgk-translate-quota-bar')) return;
    const bar = document.createElement('div');
    bar.className = `lgk-translate-quota-bar lgk-translate-quota-bar--${level}`;
    bar.setAttribute('data-lgk-no-translate', '');
    bar.setAttribute('role', 'status');
    bar.innerHTML = `<span>${message}</span><button type="button" aria-label="Fermer">×</button>`;
    bar.querySelector('button')?.addEventListener('click', dismissQuotaAlert);
    document.body.appendChild(bar);
    positionQuotaAlert();
  }

  function positionQuotaAlert() {
    const bar = document.querySelector('.lgk-translate-quota-bar');
    const cross = document.getElementById('lgk-gd-cross');
    if (!bar || !cross?.classList.contains('is-visible')) return;
    const h = cross.offsetHeight || 88;
    bar.style.bottom = `${h + 12}px`;
  }

  async function checkQuotaAlert() {
    if (CFG.showQuotaAlert === false) return;
    if (!isMerchantView()) return;
    try {
      if (sessionStorage.getItem(STORAGE_QUOTA_DISMISSED) === '1') return;
    } catch {
      /* ignore */
    }

    const api = CFG.apiBase?.replace(/\/$/, '');
    if (!api) return;

    try {
      const res = await fetchJson(`${api}/health`, { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      const usage = data.deeplUsage;
      if (!usage?.limit) return;

      const limit = Number(usage.limit);
      const remaining = Math.max(0, Number(usage.remaining ?? limit - Number(usage.used)));
      const ratio = limit > 0 ? remaining / limit : 0;

      if (!data.deeplQuotaAvailable || remaining === 0) {
        showQuotaAlert(
          `Traduction DeepL : quota épuisé. MyMemory (secours) est actif — qualité réduite. Envisagez DeepL Growth (~30 €/mois).`,
          'danger',
        );
        return;
      }

      if (ratio <= QUOTA_ALERT_THRESHOLD) {
        const pct = Math.round(ratio * 100);
        showQuotaAlert(
          `Traduction DeepL : il reste ${formatChars(remaining)} caractères (${pct} %). Puis bascule auto sur MyMemory.`,
          'warning',
        );
      }
    } catch {
      /* ignore */
    }
  }

  function watchDynamic() {
    if (observer) return;
    let timer;
    observer = new MutationObserver(() => {
      if (busy || translating || activeLang === SOURCE || activeLang === contentBaseLang) return;
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (!busy && !translating) {
          translateFullSite(activeLang, contentBaseLang)
            .then(() => {
              contentBaseLang = activeLang;
            })
            .catch(() => {});
        }
      }, 1200);
    });
    siteRoots().forEach((r) => observer.observe(r, { childList: true, subtree: true }));
  }

  async function init() {
    try {
      localStorage.removeItem('lgk_translate_cache_v1');
      localStorage.removeItem('lgk_translate_cache_v2');
    } catch {
      /* ignore */
    }

    bindSwitcher();
    watchDynamic();

    try {
      const saved = localStorage.getItem(STORAGE_LANG) || SOURCE;
      const manual = localStorage.getItem(STORAGE_MANUAL) === '1';

      if (await runGeoAuto(manual)) return;

      const geoHint = (() => {
        try {
          return sessionStorage.getItem(STORAGE_GEO_COUNTRY) || CFG.currentCountry || null;
        } catch {
          return CFG.currentCountry || null;
        }
      })();

      // Jamais d'URL /en : si on y est, sortir. Sinon DOM translate si besoin.
      if (isOnEnPath()) {
        setWantEnCookie(false);
        localStorage.setItem(STORAGE_LANG, SOURCE);
        try {
          localStorage.removeItem(STORAGE_MANUAL);
        } catch {
          /* ignore */
        }
        if (submitLocalizationForm(geoHint && FRENCH_MARKET_COUNTRIES.has(geoHint) ? geoHint : 'FR', 'fr')) {
          return;
        }
        window.location.replace(frPathFromCurrent());
        return;
      }

      if (saved === 'en') {
        // Traduction DOM anglaise sans changer l'URL
        activeLang = 'en';
        contentBaseLang = SOURCE;
        setHtmlLang('en');
        updateSwitcherUI('en');
        setWantEnCookie(false);
        setBusy(true);
        try {
          await runDomTranslation('en', SOURCE);
        } catch (err) {
          console.warn('[LGK Translate] init en dom', err);
        } finally {
          setBusy(false);
        }
        return;
      }

      activeLang = localStorage.getItem(STORAGE_LANG) || SOURCE;
      contentBaseLang = SOURCE;
      setHtmlLang(activeLang);
      updateSwitcherUI(activeLang);

      if (manual && activeLang !== SOURCE && activeLang !== 'en') {
        setBusy(true);
        try {
          await runDomTranslation(activeLang, SOURCE);
        } catch (err) {
          console.warn('[LGK Translate] init saved lang', err);
        } finally {
          setBusy(false);
        }
      }
    } finally {
      checkQuotaAlert();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.LGKTranslate = { applyLanguage, getLang: () => activeLang, closeAllPanels };
})();
